<template>
  <div>
    <div
      v-for="(item, index) in list"
      :key="index"
      class="mb-6 rounded-md border border-slate-200 bg-white p-3 px-4"
    >
      <div v-if="question.allows?.type">
        <FormSelectDropdown
          :options="question.allows.type"
          label="Education Type"
          v-model="item.type"
          class="mb-4"
          @update:modelValue="updateType(item, $event)"
        />
      </div>
      <FormInput
        v-if="question.allows?.primary &&  item.type !== ITEM_TYPE_HIGH_SCHOOL"
        :label="question.allows.primary"
        :placeholder="question.allows.primary"
        v-model="item.primary"
        class="mb-4"
      />
      <FormDropdownSearchAsync v-if="question.allows?.primaryList"
                               v-model="item.primary"
                               allow-support-request
                               :support-request-type="TYPE_JOB_TITLE"
                               placeholder="Select an opportunity title"
                               :label="question.allows.primaryList"
                               :get-options="(params) => question.allows.getPrimaryList(params)"
                               class="mb-4 w-full flex-1"/>
      <FormInput
        v-if="question.allows?.title"
        :label="question.allows.title"
        :placeholder="question.allows.title"
        v-model="item.title"
        class="mb-4"
      />
      <FormInput
        v-if="question.allows?.subtitle"
        :label="question.allows.subtitle"
        :placeholder="question.allows.subtitle"
        v-model="item.subtitle"
        class="mb-4"
      />
      <div v-if="question.allows?.completion" class="mb-4 text-slate-600">
        <FormRadio
          v-for="completionLevel in question.allows.completion"
          :key="completionLevel"
          :name="`${question.storeField}.completion.${index}`"
          class="mb-2"
          :model-value="item.completion"
          @update:modelValue="updateCompletion(item, $event)"
          :option="completionLevel"
        />
      </div>
      <div
        v-if="question.allows?.start"
        class="mb-4 flex flex-col justify-between gap-4 md:flex-row"
      >
        <FormDate
          disable-utc
          v-if="question.allows?.start"
          :label="question.allows.start"
          :placeholder="question.allows.start"
          month-picker
          class="w-full date-picker-container"
          popoverPlacement="bottom"
          :model-value="itemStart(item)"
          @update:modelValue="updateStart(item, $event)"
        />
        <FormDate
          disable-utc
          v-if="question.allows?.end"
          :label="question.allows.end + (itemIsCompletableButIsNotCompleted(item) ? ' (Optional)' : '')"
          :placeholder="(itemIsCompletableButIsNotCompleted(item))? 'Projected End' : 'End date'"
          month-picker
          class="w-full date-picker-container"
          popoverPlacement="bottom"
          :model-value="itemEnd(item)"
          @update:modelValue="updateEnd(item, $event)"
        />
      </div>
      <FormTextarea
        v-if="question.allows?.description"
        :label="question.allows.description"
        :placeholder="question.allows.description"
        v-model="item.description"
        :default-height="80"
      />
      <div v-if="question.allows?.tenure" class="mb-6">
        <FormLabel label="Tenure"/>
        <p class="-mt-1">{{ formatDaysReadable(item.tenure ? item.tenure * 365 : 0) }}</p>
      </div>
      <div v-if="question.allows?.rank">
        <FormSlider :label="question.allows.rank" v-model="item.rank"/>
        <p class="my-4 text-center text-slate-600">
          {{ getRankMessage(item.rank) }}
        </p>
      </div>
      <FormCheckbox v-if="question.allows?.honors" v-model="item.honors">
        {{ question.allows?.honors }}
      </FormCheckbox>
      <UtilityDeleteIcon class="mx-auto mt-6" @delete="removeItem(index)" has-slot>
        Remove
      </UtilityDeleteIcon>
    </div>
    <button type="button"
      @click="addItem"
      class="mx-auto mt-8 flex cursor-pointer select-none items-center justify-center gap-1.5 text-primary-600 hover:underline"
      :class="list && list.length < 1  ? 'mb-4' : 'mb-2'">
      <PlusCircleIcon class="h-4 w-4"/>
      Add Item
    </button>
  </div>
</template>

<script setup>
import FormInput from "./FormInput.vue";
import FormDate from "./FormDate.vue";
import FormTextarea from "./FormTextarea.vue";
import FormSlider from "./FormSlider.vue";
import FormRadio from "./FormRadio.vue";
import FormSelectDropdown from "./FormSelectDropdown.vue";
import UtilityDeleteIcon from "./UtilityDeleteIcon.vue";
import FormLabel from "./FormLabel.vue";
import FormDropdownSearchAsync from "./FormDropdownSearchAsync.vue";
import FormCheckbox from "./FormCheckbox.vue";
import {PlusCircleIcon} from "@heroicons/vue/20/solid/index.js";
import {computed} from "vue";
import {differenceBetweenDates, formatDaysReadable,} from "../helpers/FormatDates.js";
import {getRankMessage} from "../helpers/CandidateFunctions.js";
import {TYPE_JOB_TITLE} from "../constants/SupportRequests.js";

const props = defineProps({
  question: Object,
  useStore: Function,
  initializeEmpty: {
    type: Boolean,
    default: true,
  },
});

const store = props.useStore();

const ITEM_TYPE_HIGH_SCHOOL = "High School/GED";

const list = computed(() => store.state[props.question.storeField]);

const itemIsCompletableButIsNotCompleted = (item) => {
  return ["Partially Completed", "Currently Attending", "Currently Working Here"].includes(item.completion);
};

const addItem = () => {
  if (props.question.max && list.value.length >= props.question.max) {
    toast.info(`You can only add up to ${props.question.max} items`);
    return;
  }

  let item = {
    ...(props.question.allows?.primary && {primary: ""}),
    ...(props.question.allows?.title && {title: ""}),
    ...(props.question.allows?.subtitle && {subtitle: ""}),
    ...(props.question.allows?.start && {start: null}),
    ...(props.question.allows?.end && {end: null}),
    ...(props.question.allows?.description && {description: ""}),
    ...(props.question.allows?.rank && {rank: 1}),
    ...(props.question.allows?.completion && {completion: null}),
    ...(props.question.allows?.tenure && {tenure: 0}),
    ...(props.question.allows?.type && {type: ""}),
  };
  list.value.push(item);
};

if (props.initializeEmpty && !list.value.length) addItem();

const removeItem = (index) => list.value.splice(index, 1);

const itemStart = (item) => item.start ? new Date(item.start) : null;
const itemEnd = (item) => item.end ? new Date(item.end) : null;

const updateStart = (item, start) => {
  if (!item?.end || !item?.start) item.tenure = null;
  else item.tenure = differenceBetweenDates(item.start, item.end, "years");
  item.start = start;
};

const updateEnd = (item, end) => {
  if (!item?.end || !item?.start) item.tenure = null;
  else item.tenure = differenceBetweenDates(item.start, item.end, "years");
  item.end = end;
};

const updateType = (item, type) => {
  if (type === ITEM_TYPE_HIGH_SCHOOL) item.primary = "";
  item.type = type;
};

const updateCompletion = (item, completion) => {
  if (completion === "Graduated") toast.info("Official graduation may be verified before hire");
  item.completion = completion;
  if (itemIsCompletableButIsNotCompleted(item)) item.end = null;
};
</script>

<style scoped>
@media (min-width: 768px) {
  .date-picker-container {
    max-width: calc(50% - 0.5rem);
  }
}
</style>
